import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Users, Calendar, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface Workspace {
  id: string;
  name: string;
  slug?: string;
  status?: string;
  plan?: { 
    id: string;
    name: string;
    code: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  created_at: string;
  members?: any[];
  trial?: {
    status: string;
    ends_at: string;
  };
  credit_wallet?: {
    credits_balance: number;
  };
}

interface EditWorkspaceModalProps {
  workspace: Workspace | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

export function EditWorkspaceModal({ workspace, isOpen, onClose, onUpdate }: EditWorkspaceModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    status: 'active',
    plan_code: 'trial'
  });

  // Fetch available plans
  const { data: plans = [] } = useQuery({
    queryKey: ['available-plans'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('workspace-plans', {
        body: { action: 'list_plans' }
      });
      if (error) throw error;
      return data.plans || [];
    }
  });

  // Update workspace mutation
  const updateWorkspaceMutation = useMutation({
    mutationFn: async (updates: any) => {
      const { data, error } = await supabase.functions.invoke('workspaces-manage', {
        body: {
          action: 'update_workspace',
          workspace_id: workspace?.id,
          updates
        }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Workspace updated successfully');
      queryClient.invalidateQueries({ queryKey: ['multi-tenant-workspaces'] });
      queryClient.invalidateQueries({ queryKey: ['multi-tenant-stats'] });
      onUpdate();
      onClose();
    },
    onError: (error: any) => {
      toast.error(`Failed to update workspace: ${error.message}`);
    }
  });

  useEffect(() => {
    if (workspace && isOpen) {
      setFormData({
        name: workspace.name,
        status: workspace.status || 'active',
        plan_code: workspace.plan?.code || 'trial'
      });
    }
  }, [workspace, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspace) return;

    updateWorkspaceMutation.mutate({
      name: formData.name.trim(),
      status: formData.status,
      plan_code: formData.plan_code
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getPlanColor = (planCode: string) => {
    switch (planCode) {
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'customer': return 'bg-blue-100 text-blue-800';
      case 'trial': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!workspace) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Building className="h-5 w-5" />
            <span>Edit Workspace</span>
          </DialogTitle>
          <DialogDescription>
            Update workspace settings and configuration
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Status Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Current Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge className={getStatusColor(workspace.status || 'active')}>
                  {workspace.status || 'active'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Plan</span>
                <Badge className={getPlanColor(workspace.plan?.code || 'trial')}>
                  {workspace.plan?.name || 'Trial'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Members</span>
                <div className="flex items-center space-x-1">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span className="text-sm">{workspace.members?.length || 0}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Created</span>
                <span className="text-sm">
                  {new Date(workspace.created_at).toLocaleDateString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Edit Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Workspace Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter workspace name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span>Active</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="suspended">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span>Suspended</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="archived">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-gray-500" />
                      <span>Archived</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="plan">Plan</Label>
              <Select value={formData.plan_code} onValueChange={(value) => setFormData(prev => ({ ...prev, plan_code: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((plan: any) => (
                    <SelectItem key={plan.code} value={plan.code}>
                      <div className="flex items-center justify-between w-full">
                        <span>{plan.name}</span>
                        <Badge variant="outline" className="ml-2">
                          {plan.credits_per_month} credits
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </form>
        </div>

        {/* Trial Status Warning */}
        {workspace.trial && workspace.trial.status === 'active' && (
          <div className="flex items-center space-x-2 p-3 bg-warning/10 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-warning" />
            <div className="text-sm">
              <span className="font-medium">Trial workspace:</span> {' '}
              Expires {new Date(workspace.trial.ends_at).toLocaleDateString()}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            disabled={updateWorkspaceMutation.isPending}
          >
            {updateWorkspaceMutation.isPending ? 'Updating...' : 'Update Workspace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}